/** Device binding metadata only. No message content or business records are persisted. */
export interface PushBinding {
    user_id: number;
    binding_id: string;
    token: string;
    endpoint: string;
    enabled: boolean;
}
export const INBOX_KINDS = ["pending", "notifications", "messages", "activities"] as const;
export type InboxKind = typeof INBOX_KINDS[number];
export type InboxCounts = Record<InboxKind, number>;
export function inboxTotal(counts: Partial<InboxCounts>): number {
    return Math.min(0xffffffff, INBOX_KINDS.reduce((sum, key) => {
        const value = counts[key];
        return sum + (typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0);
    }, 0));
}
async function bindingStore<T>(write: boolean, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open("promaster-push", 1);
        request.onupgradeneeded = () => request.result.createObjectStore("device");
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error("Device storage is unavailable"));
    });
    try {
        return await new Promise<T>((resolve, reject) => {
            const tx = db.transaction("device", write ? "readwrite" : "readonly");
            const request = action(tx.objectStore("device"));
            tx.oncomplete = () => resolve(request.result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } finally { db.close(); }
}
export const readPushBinding = () => bindingStore<PushBinding | undefined>(false, store => store.get("binding"));
export const writePushBinding = (binding: PushBinding, current = () => true) => bindingStore(true,
    store => current() ? store.put(binding, "binding") : store.get("binding"));
export const deletePushBinding = () => bindingStore(true, store => store.delete("binding"));

export async function setBadge(count: number) {
    const badges = navigator as Navigator & {
        setAppBadge?(value: number): Promise<void>;
        clearAppBadge?(): Promise<void>;
    };
    try {
        if (count > 0) await badges.setAppBadge?.(count);
        else if (badges.clearAppBadge) await badges.clearAppBadge();
        else await badges.setAppBadge?.(0);
    } catch { /* Badges depend on the operating system and its permissions. */ }
}
